import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { getKey } from './AddTranslationUI'
import DialogWrapper from '../../../../components/core-ui/DialogWrapper'
import { serverActionService } from '../../../../services/serverActionService'
import { useAuthContext } from '../../../../hooks/useAuthContext'

const SetupInboundTranslation = ({ fields, open, onClose, onSave }) => {
  const { user } = useAuthContext()

  const [fieldKeyPair, setFieldKeyPair] = useState([])
  const [exceptions, setExceptions] = useState({})

  useEffect(() => {
    setFieldKeyPair(
      fields.map((it) => ({
        key: it.key ? it.key : getKey(it.field_template_name),
        field_template_id: it.field_template_id,
        field_template_name: it.field_template_name,
      })),
    )

    setExceptions({})
  }, [fields])

  /*
    ---------------------------------
    DUPLICATE KEY DETECTION (LIVE)
    ---------------------------------
  */
  const duplicateKeys = useMemo(() => {
    const keys = fieldKeyPair
      .map((it) => it.key)
      .filter(Boolean)
      .map((it) => it.trim().toLowerCase())

    const countMap = {}

    keys.forEach((key) => {
      countMap[key] = (countMap[key] || 0) + 1
    })

    return Object.keys(countMap).filter((key) => countMap[key] > 1)
  }, [fieldKeyPair])

  const isDuplicate = (key) => {
    if (!key) return false

    return duplicateKeys.includes(key.trim().toLowerCase())
  }

  const saveChanges = async () => {
    try {
      if (duplicateKeys.length > 0) {
        return toast.error(`Duplicate keys found: ${duplicateKeys.join(', ')}`)
      }

      const res = await serverActionService.saveInboundActionTranslations(user, fieldKeyPair)

      setExceptions(res.exceptions || {})

      toast.success('Key changes saved successfully!')
      onSave(fieldKeyPair)
    } catch (error) {
      toast.error('Something went wrong!')
    }
  }

  const updateKey = (fieldTemplateId, value) => {
    setFieldKeyPair((prev) =>
      prev.map((it) =>
        it.field_template_id === fieldTemplateId
          ? {
              ...it,
              key: value,
            }
          : it,
      ),
    )
  }

  return (
    <DialogWrapper actions={null} open={open} onClose={onClose} fullWidth>
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Setup Inbound Keys
        </Typography>

        <TableContainer sx={{ mt: 2, mb: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Key</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {fieldKeyPair.map((pair) => {
                const hasApiError = !!exceptions[pair.field_template_id]

                const duplicate = isDuplicate(pair.key)

                return (
                  <Tooltip
                    key={pair.field_template_id}
                    title={exceptions[pair.field_template_id] || ''}
                    arrow
                  >
                    <TableRow
                      sx={{
                        backgroundColor: duplicate
                          ? 'rgba(255,0,0,0.08)'
                          : hasApiError
                            ? '#ffb0b8'
                            : 'inherit',
                      }}
                    >
                      <TableCell>{pair.field_template_name}</TableCell>

                      <TableCell
                        sx={{
                          p: 0.5,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          value={pair.key || ''}
                          error={duplicate}
                          helperText={duplicate ? 'Duplicate key' : ''}
                          onChange={(e) => updateKey(pair.field_template_id, e.target.value)}
                        />
                      </TableCell>
                    </TableRow>
                  </Tooltip>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {duplicateKeys.length > 0 && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            Duplicate Keys: {duplicateKeys.join(', ')}
          </Typography>
        )}

        <Button
          size="small"
          color="primary"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={duplicateKeys.length > 0}
          onClick={saveChanges}
        >
          Save Changes
        </Button>
      </Box>
    </DialogWrapper>
  )
}

export default SetupInboundTranslation
